import React from 'react';
import { concat, of, Subject } from 'rxjs';
import { createBrowserHistory, History } from 'history';
import { share } from 'rxjs';
import { ApolloProvider } from '@apollo/client';
import { Provider as ReduxProvider } from 'react-redux';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router';
import { GQL_STATE_ID, REDUX_STATE_ID } from 'src/core/config';
import { DocumentNode } from 'graphql';
import { ApolloLink } from '@apollo/client';

import { createBrowserStore, OpenDeps } from '../store';
import { registerRuntime, RuntimeEnv } from '../runtime';
import { RegisterContextProvider } from '../components';
import {
    getRuntimeState,
    resolve,
    ResolvedComponent,
    resolveWeak,
    ResolveWeakParams,
    ResolveParams,
} from '../utils/resolve';
import { initialState as initialRuntimeState } from '../runtime';
import { storageRegister } from '../storage';
import {
    AsyncLoader,
    AsyncRouter,
    historyAsObs,
    routerMiddleware,
    routerRegister,
    initialState as initialRouterState,
} from '../router';
import { readJson } from '../utils/readJson';
import { getBrowserApolloClient } from '../apollo/getBrowserApolloClient';
import { cheapClone } from '../utils/general';
import { resolvers } from '../apollo';
import { RegisterItem } from '../types';

export { APP_ENV_STATE_ID } from 'src/core/config';

interface ApolloBrowserParams<Env extends RuntimeEnv = RuntimeEnv> {
    components: ResolveParams['components'];
    asyncLoader: ResolveParams['asyncLoader'];
    knownRoutes: ResolveParams['knownRoutes'];
    urlResolver: DocumentNode;
    resolveWeakLoader: ResolveWeakParams['resolveWeakLoader'];
    features: RegisterItem[];
    links: ApolloLink[];
    initialState: { [index: string]: any };
    deps: OpenDeps;
    env: Env;
    unionAndInterfaceTypes?: { [index: string]: any };
}

export function initBrowserApollo(params: ApolloBrowserParams) {
    const {
        asyncLoader,
        resolveWeakLoader,
        features,
        links,
        initialState,
        components,
        deps,
        urlResolver,
        knownRoutes,
        env,
        unionAndInterfaceTypes = {},
    } = params;

    const reduxState = readJson(REDUX_STATE_ID);
    const [client] = getBrowserApolloClient([...links], unionAndInterfaceTypes, readJson(GQL_STATE_ID));

    const { resolveFromNetwork, syncReadFromClient } = resolvers(client, urlResolver);

    const resolveLocal = resolve({
        asyncLoader,
        components: components,
        knownRoutes: knownRoutes,
        urlQuery: urlResolver,
        resolveFromNetwork,
    });

    /**
     * Try to load an initial component that may already be available
     * due to SSR
     */
    const initial: ResolvedComponent = resolveWeak({
        knownRoutes: knownRoutes,
        resolveWeakLoader,
        urlKey: window.location.pathname,
        syncReadFromClient,
    });

    // const appEnv: Partial<RuntimeEnv> = readJson('app-env');
    const error$ = new Subject<{ error: Error; info: React.ErrorInfo }>();

    const history = createBrowserHistory();
    const historyEvents$ = historyAsObs(history).pipe(share());

    /**
     * Create the redux store
     */
    const { store, register, registerEpic, epicDeps } = createBrowserStore({
        createMiddleware: (mw) => [routerMiddleware(history), ...mw] as any,
        registrations: [registerRuntime(), routerRegister(), storageRegister(), ...features],
        deps: {
            client: client as any,
            error$,
            history,
            historyEvents$,
            ...deps,
        },
        env,
        initialState: {
            router: {
                ...initialRouterState,
                location: {
                    ...initialRouterState.location,
                    pathname: window.location.pathname,
                    search: window.location.search,
                },
            },
            runtime: {
                ...initialRuntimeState,
            },
            ...reduxState,
            ...initialState,
        },
    });

    const onlineOutdated$ = getRuntimeState(store, window.navigator.onLine, false);

    const nav$ = initial.Cmp
        ? historyEvents$
        : concat(
              of(
                  {
                      location: { pathname: 'PAGE_LOAD', hash: '', search: '', key: '', state: {} },
                      action: history.action,
                  },
                  { location: history.location, action: history.action },
              ),
              historyEvents$,
          );

    const complete$ = new Subject();

    return () => {
        const App: React.FC<React.PropsWithChildren> = React.memo((props) => {
            return (
                <ApolloProvider client={client}>
                    <RegisterContextProvider register={register} registerEpic={registerEpic} epicDeps={epicDeps}>
                        <ReduxProvider store={store}>
                            <HistoryRouter history={history as any}>
                                <AsyncRouter history={history as History} complete$={complete$}>
                                    {props.children}
                                </AsyncRouter>
                            </HistoryRouter>
                        </ReduxProvider>
                    </RegisterContextProvider>
                </ApolloProvider>
            );
        });
        const Async = (
            <AsyncLoader
                nav$={nav$}
                resolveLocal={resolveLocal}
                complete$={complete$}
                // Pass through even when Cmp is null so AsyncLoader can use componentName
                // for the correct skeleton while the root chunk loads (hard refresh).
                initial={initial.componentName || initial.Cmp ? initial : null}
                initialLocation={cheapClone(history.location)!}
                initialAction={history.action}
                onlineOutdated$={onlineOutdated$}
            />
        );
        return { App, Async, store };
    };
}
